'use client';

import * as React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { cn } from '../lib/cn';

/**
 * DropdownMenu, Radix-backed and custom-styled.
 *
 * Mirrors the shadcn pattern + the visual language of NotificationsPopover
 * (rounded-md, border, shadow-lg, bg-surface-elevated, animate-fade-in).
 *
 * Re-exports Radix's Root / Trigger / Portal / Group plus styled
 * Content / Item / Label / Separator wrappers.
 *
 * a11y:
 *   - Keyboard navigation (Tab / arrows / Space / Enter) handled by Radix
 *   - Items close the menu on activation by default
 *   - Destructive variant on Item uses the error color token
 *
 * RTL: positioning via `align`/`side` is logical; padding uses ps-/pe-
 * (logical) so the menu flips correctly in Arabic.
 */

export const DropdownMenu = DropdownMenuPrimitive.Root;
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
export const DropdownMenuGroup = DropdownMenuPrimitive.Group;
export const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

export const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 6, align = 'end', ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      align={align}
      className={cn(
        'z-50 min-w-[200px] overflow-hidden',
        'rounded-md border border-border-subtle bg-surface-elevated shadow-lg',
        'p-1 outline-none',
        'data-[state=open]:animate-fade-in',
        className,
      )}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

export interface DropdownMenuItemProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> {
  /** "destructive" renders with the error color (used for Sign Out etc). */
  variant?: 'default' | 'destructive';
}

export const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  DropdownMenuItemProps
>(({ className, variant = 'default', ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-pointer select-none items-center gap-2',
      // NOTE: max-sm:py-2.5 in this BASE outranks any consumer's unprefixed
      // py-* below the sm breakpoint (variant ordering). Today's only consumer
      // (ProfileMenu) passes none; if a future consumer needs custom vertical
      // padding on mobile, it must use its own max-sm: variant.
      'rounded-sm px-2 py-1.5 max-sm:py-2.5 text-sm outline-none',
      'transition-colors duration-fast',
      'focus:bg-sand-50 focus:text-text-primary',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      variant === 'destructive'
        ? 'text-error-700 focus:bg-error-50 focus:text-error-700'
        : 'text-text-secondary hover:text-text-primary',
      className,
    )}
    {...props}
  />
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

export const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn('px-2 py-1.5 text-xs font-medium text-text-muted', className)}
    {...props}
  />
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

export const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn('my-1 h-px bg-border-subtle', className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;
